/**
 * MultiParcelDrawer — N-polygon boundary editor (MP Phase 3).
 *
 * Shown when the site has parcels.length >= 2. Single-parcel sites
 * continue using SiteBoundaryDrawer unchanged.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { Button } from '../ui/button';
import {
  Trash2,
  Check,
  Plus,
  Undo2,
  Redo2,
  Hand,
  Ruler,
  Merge,
  Scissors,
  X,
  Square,
  Magnet,
  Compass,
} from 'lucide-react';
import { SiteLayerOverlays } from './SiteMapLayers';
import {
  StudyAreaSelectedOverlays,
  BoundaryAlignmentPreviewOverlay,
  polygonDisplayPaths,
} from './BoundaryStudyAreaOverlays';
import { useTouchHint } from '../../lib/useTouchHint';
import {
  previewSquarePolygonCorners,
  previewAxisAlignedRectangle,
  snapAndAlignVertex,
  fitMapToViewportBounds,
  type ResolvedBoundaryStudyPolygon,
  type SnapGuide,
  type SquareCornersPreview,
} from '../../lib/boundaryAlignmentTools';
import { PARCEL_COLORS } from '../../lib/parcelColors';
import {
  computeAggregate,
  polygonArea,
  polygonCentroid,
  polygonPerimeter,
  parcelArea,
  parcelPerimeter,
} from '../../lib/siteBoundaryGeometry';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import type { BoundaryResult } from './SiteBoundaryDrawer';
import { fetchPolygonElevationsCascading } from '../../lib/fetchPolygonElevationsCascading';
import { AddressLabelOverrideControl } from './AddressLabelOverrideControl';
import { evaluateStudyPolygon } from '../../lib/siteStudyArea';
import {
  buildTopologyTelemetry,
  logParcelTopologyChange,
  mergeParcelsLocal,
  splitParcelLocal,
  type ParcelHistoryEntry,
} from '../../lib/parcelDrawerActions';

export type { BoundaryResult };

const PRIMARY_STROKE = '#2563eb';

interface MultiParcelDrawerProps {
  center: { lat: number; lng: number };
  onConfirm: (result: BoundaryResult) => void | Promise<void>;
  onSkip: () => void;
  /** Initial parcels — must have length >= 2. */
  initialParcels: SiteBoundaryParcel[];
  apiKey: string;
  /** When set, topology audit telemetry is logged only after explicit confirmation. */
  siteId?: string;
  existingLayers?: import('../../types/site').SiteLayer[];
  addressLabel?: string;
  canonicalAddress?: string;
  onSaveLabel?: (label: string | undefined) => Promise<void>;
  /** Study-area-selected layers rendered in blue (`#2563eb`) regardless of `layer.visible`. */
  studyAreaPolygons?: readonly ResolvedBoundaryStudyPolygon[];
  /** Additional viewport points (e.g. union of visible layers + study-area layers). */
  viewportExtraPoints?: readonly [number, number][];
}

function pathToCoords(path: google.maps.MVCArray<google.maps.LatLng>): Array<{ lat: number; lng: number }> {
  const out: Array<{ lat: number; lng: number }> = [];
  for (let i = 0; i < path.getLength(); i++) {
    const p = path.getAt(i);
    out.push({ lat: p.lat(), lng: p.lng() });
  }
  return out;
}

function parcelFromPolygon(
  poly: google.maps.Polygon,
  existing?: SiteBoundaryParcel,
  index?: number,
): SiteBoundaryParcel {
  const coords = pathToCoords(poly.getPath());
  const paths = typeof poly.getPaths === 'function' ? poly.getPaths() : null;
  const holes = paths
    ? Array.from({ length: Math.max(0, paths.getLength() - 1) }, (_, i) => pathToCoords(paths.getAt(i + 1)))
    : existing?.holes;
  return {
    id: existing?.id ?? `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    label: existing?.label ?? `Parcel ${(index ?? 0) + 1}`,
    polygon: coords,
    ...(holes?.length ? { holes } : {}),
    area: parseFloat(parcelArea(coords, holes).toFixed(1)),
    perimeter: parseFloat(parcelPerimeter(coords, holes).toFixed(2)),
    centroid: polygonCentroid(coords),
    intendedUse: existing?.intendedUse,
    ownership: existing?.ownership,
    notes: existing?.notes,
  };
}

/** Validate exclusion topology without replacing the user's rings with a normalized outline. */
function holeGeometryError(parcel: SiteBoundaryParcel): string | null {
  if (!parcel.holes?.length) return null;
  const evaluated = evaluateStudyPolygon(parcel.polygon, { holes: parcel.holes });
  if ('error' in evaluated) return evaluated.error;
  // The authority unions/clips rings. An area difference means exclusions lie outside
  // the outer ring or overlap; accepting that normalization would change their meaning.
  if (Math.abs(evaluated.areaAcres - parcelArea(parcel.polygon, parcel.holes)) > 0.001) {
    return 'Exclusion rings must remain inside the parcel and must not overlap. Review the outline before saving.';
  }
  return null;
}

function cloneParcels(parcels: SiteBoundaryParcel[]): SiteBoundaryParcel[] {
  return structuredClone(parcels);
}

function TouchHintOverlay() {
  const { show, dismiss } = useTouchHint('multi-parcel-drawer-touch-hint');
  if (!show) return null;
  return (
    <button
      type="button"
      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 bg-black/70 text-white text-xs rounded-full"
      onClick={dismiss}
    >
      <Hand className="w-3.5 h-3.5" />
      Tap to add vertices · drag handles to adjust
    </button>
  );
}

export const MultiParcelDrawer: React.FC<MultiParcelDrawerProps> = ({
  center,
  onConfirm,
  onSkip,
  initialParcels,
  siteId,
  existingLayers,
  addressLabel,
  canonicalAddress,
  onSaveLabel,
  studyAreaPolygons = [],
  viewportExtraPoints = [],
}) => {
  const map = useMap('multi-parcel-map');

  const [parcels, setParcels] = useState<SiteBoundaryParcel[]>(initialParcels);
  const [parcelsRevision, setParcelsRevision] = useState(0);
  const [activeId, setActiveId] = useState<string>(initialParcels[0]?.id ?? '');
  const [selectedParcelIds, setSelectedParcelIds] = useState<string[]>([]);
  const [splitMode, setSplitMode] = useState(false);
  const [splitLineStart, setSplitLineStart] = useState<{ lat: number; lng: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [undoStack, setUndoStack] = useState<ParcelHistoryEntry[]>([]);
  const [redoStack, setRedoStack] = useState<ParcelHistoryEntry[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);

  const polygonsRef = useRef<Record<string, google.maps.Polygon>>({});
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const splitLineRef = useRef<google.maps.Polyline | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Migration (maps-migration): the deprecated Maps drawing-library
  // manager was removed from Maps JS v3.65. New parcels are now captured
  // with click-to-add-vertex on the parent <Map> (the SiteLayerDrawOverlay
  // pattern): a live preview google.maps.Polygon + a map "click" listener
  // appending vertices, finished on double-click, first-vertex close, or
  // the explicit Finish control. The resulting parcel (via
  // `parcelFromPolygon`) + history/selection wiring are identical to the
  // prior polygon-complete path.
  const [drawVertices, setDrawVertices] = useState<{ lat: number; lng: number }[]>([]);
  const drawVerticesRef = useRef<{ lat: number; lng: number }[]>([]);
  const previewRef = useRef<google.maps.Polygon | null>(null);
  const drawListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  // Live mirror of `isDrawing` for the persistent map listeners.
  const isDrawingRef = useRef(false);

  // Opt-in alignment / snapping / orthogonal / square tools (#244 #issuecomment-6607686195, #issuecomment-6607716290)
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [alignmentGuidesEnabled, setAlignmentGuidesEnabled] = useState(false);
  const [orthogonalConstraintEnabled, setOrthogonalConstraintEnabled] = useState(false);
  const snapConfigRef = useRef({
    snapEnabled: false,
    alignmentGuidesEnabled: false,
    orthogonalConstraintEnabled: false,
  });
  useEffect(() => {
    snapConfigRef.current = {
      snapEnabled,
      alignmentGuidesEnabled,
      orthogonalConstraintEnabled,
    };
  }, [snapEnabled, alignmentGuidesEnabled, orthogonalConstraintEnabled]);

  const [activeGuides, setActiveGuides] = useState<SnapGuide[]>([]);
  const [squarePreview, setSquarePreview] = useState<SquareCornersPreview | null>(null);
  const [squarePreviewMode, setSquarePreviewMode] = useState<'square' | 'rectangle' | null>(null);
  const [geometryError, setGeometryError] = useState<string | null>(null);
  const suppressSnapSyncRef = useRef(false);

  const studyAreaReferencePolygons = useMemo(
    () => studyAreaPolygons.map((p) => p.points),
    [studyAreaPolygons],
  );
  const parcelsRef = useRef<SiteBoundaryParcel[]>(parcels);
  useEffect(() => {
    parcelsRef.current = parcels;
  }, [parcels]);

  const getReferencePolygonsForParcel = useCallback(
    (excludeParcelId?: string): [number, number][][] => {
      const siblingPolys = parcelsRef.current
        .filter((p) => p.id !== excludeParcelId)
        .map((p) => p.polygon.map((pt) => [pt.lat, pt.lng] as [number, number]));
      return [...siblingPolys, ...studyAreaReferencePolygons];
    },
    [studyAreaReferencePolygons],
  );

  // Fit viewport to union of parcels + studyAreaPolygons + viewportExtraPoints on mount
  useEffect(() => {
    if (!map) return;
    const allPts: [number, number][] = [
      ...initialParcels.flatMap((p) =>
        p.polygon.map((pt) => [pt.lat, pt.lng] as [number, number]),
      ),
      ...studyAreaPolygons.flatMap((p) => p.points),
      ...viewportExtraPoints,
    ];
    if (allPts.length > 0) {
      fitMapToViewportBounds(map, allPts);
    }
  }, [map, initialParcels, studyAreaPolygons, viewportExtraPoints]);

  const aggregate = computeAggregate(parcels);
  const selectedHasHoles = parcels.some(p => selectedParcelIds.includes(p.id) && p.holes?.length);
  const canMerge = selectedParcelIds.length >= 2 && !mergeBusy && !splitMode && !selectedHasHoles;
  const canSplit = selectedParcelIds.length === 1 && !mergeBusy && !splitMode && !selectedHasHoles;

  const bumpParcels = useCallback((next: SiteBoundaryParcel[]) => {
    setParcels(next);
    setParcelsRevision(r => r + 1);
  }, []);

  const pushHistoryEntry = useCallback((
    before: SiteBoundaryParcel[],
    after: SiteBoundaryParcel[],
    kind: ParcelHistoryEntry['kind'],
  ) => {
    setUndoStack(s => [...s.slice(-19), { kind, before: cloneParcels(before), after: cloneParcels(after) }]);
    setRedoStack([]);
  }, []);

  const syncParcelsFromMap = useCallback((parcelId: string) => {
    const poly = polygonsRef.current[parcelId];
    if (!poly) return;
    setSelectedParcelIds([]);
    setParcels(prev => {
      const idx = prev.findIndex(p => p.id === parcelId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = parcelFromPolygon(poly, prev[idx], idx);
      pushHistoryEntry(prev, next, 'edit');
      setParcelsRevision(r => r + 1);
      return next;
    });
  }, [pushHistoryEntry]);

  const attachPathListeners = useCallback((parcelId: string, poly: google.maps.Polygon) => {
    const onVertexEdit = (vertexIdx?: number) => {
      if (suppressSnapSyncRef.current) return;
      const cfg = snapConfigRef.current;
      if (
        typeof vertexIdx === 'number' &&
        (cfg.snapEnabled || cfg.alignmentGuidesEnabled || cfg.orthogonalConstraintEnabled)
      ) {
        const coords = pathToCoords(poly.getPath());
        const tuples = coords.map((c) => [c.lat, c.lng] as [number, number]);
        if (tuples.length >= 3 && vertexIdx >= 0 && vertexIdx < tuples.length) {
          const candidate = tuples[vertexIdx]!;
          const prevVertex = tuples[(vertexIdx - 1 + tuples.length) % tuples.length];
          const nextVertex = tuples[(vertexIdx + 1) % tuples.length];
          const snapped = snapAndAlignVertex({
            candidate,
            activePolygon: tuples,
            activeVertexIndex: vertexIdx,
            prevVertex,
            nextVertex,
            referencePolygons: getReferencePolygonsForParcel(parcelId),
            snapToVerticesAndEdges: cfg.snapEnabled,
            enableAlignmentGuides: cfg.alignmentGuidesEnabled,
            enableOrthogonalConstraint: cfg.orthogonalConstraintEnabled,
          });
          setActiveGuides(snapped.guides);
          if (
            snapped.snapped &&
            (Math.abs(snapped.point[0] - candidate[0]) > 1e-9 ||
              Math.abs(snapped.point[1] - candidate[1]) > 1e-9)
          ) {
            suppressSnapSyncRef.current = true;
            poly.getPath().setAt(
              vertexIdx,
              new google.maps.LatLng(snapped.point[0], snapped.point[1]),
            );
            suppressSnapSyncRef.current = false;
          }
        }
      } else {
        setActiveGuides([]);
      }
      setSquarePreview(null);
      setSquarePreviewMode(null);
      syncParcelsFromMap(parcelId);
    };
    listenersRef.current.push(
      google.maps.event.addListener(poly.getPath(), 'set_at', (idx: number) => onVertexEdit(idx)),
      google.maps.event.addListener(poly.getPath(), 'insert_at', (idx: number) => onVertexEdit(idx)),
      google.maps.event.addListener(poly.getPath(), 'remove_at', () => onVertexEdit()),
    );
    const paths = typeof poly.getPaths === 'function' ? poly.getPaths() : null;
    if (paths) for (let i = 1; i < paths.getLength(); i++) {
      for (const event of ['set_at', 'insert_at', 'remove_at']) {
        listenersRef.current.push(google.maps.event.addListener(paths.getAt(i), event, () => onVertexEdit()));
      }
    }
  }, [syncParcelsFromMap, getReferencePolygonsForParcel]);

  const toggleSelection = useCallback((parcelId: string, additive: boolean) => {
    setActiveId(parcelId);
    setSelectedParcelIds(prev => {
      if (additive) {
        if (prev.includes(parcelId)) return prev.filter(id => id !== parcelId);
        return [...prev, parcelId];
      }
      if (prev.length === 1 && prev[0] === parcelId) return prev;
      return [parcelId];
    });
  }, []);

  const renderParcelOnMap = useCallback((
    parcel: SiteBoundaryParcel,
    index: number,
    mapInstance: google.maps.Map,
    selectedIds: string[],
    active: string,
  ) => {
    const colors = PARCEL_COLORS[index % PARCEL_COLORS.length];
    const isSelected = selectedIds.includes(parcel.id);
    const isActive = active === parcel.id;
    const poly = new google.maps.Polygon({
      paths: parcel.holes?.length ? polygonDisplayPaths(parcel.polygon, parcel.holes) : parcel.polygon,
      strokeColor: isSelected ? PRIMARY_STROKE : colors.stroke,
      strokeWeight: isSelected ? 2 : isActive ? 3 : 2,
      fillColor: isSelected ? PRIMARY_STROKE : colors.stroke,
      fillOpacity: isSelected || isActive ? 0.25 : 0.15,
      editable: !splitMode,
      draggable: !splitMode,
      map: mapInstance,
      zIndex: isSelected || isActive ? 2 : 1,
    });
    poly.addListener('click', (e: google.maps.MapMouseEvent) => {
      const additive = !!(e.domEvent as MouseEvent)?.shiftKey;
      toggleSelection(parcel.id, additive);
    });
    polygonsRef.current[parcel.id] = poly;
    attachPathListeners(parcel.id, poly);
  }, [attachPathListeners, splitMode, toggleSelection]);

  const clearPreview = useCallback(() => {
    previewRef.current?.setMap(null);
    previewRef.current = null;
  }, []);

  // Finalise the placed vertices into a new parcel — the exact
  // replacement for the old polygon-complete path: build the parcel via
  // `parcelFromPolygon`, push history, select it, register the editable
  // polygon, and exit drawing mode.
  const finishDrawing = useCallback((verts: { lat: number; lng: number }[]) => {
    if (!map || verts.length < 3) return;
    const idx = parcels.length;
    const poly = new google.maps.Polygon({
      paths: verts,
      strokeColor: PARCEL_COLORS[idx % PARCEL_COLORS.length].stroke,
      strokeWeight: 2,
      fillColor: PARCEL_COLORS[idx % PARCEL_COLORS.length].stroke,
      fillOpacity: 0.15,
      editable: true,
      draggable: true,
      map,
    });
    const newParcel = parcelFromPolygon(poly, undefined, idx);
    pushHistoryEntry(parcels, [...parcels, newParcel], 'edit');
    bumpParcels([...parcels, newParcel]);
    setActiveId(newParcel.id);
    setSelectedParcelIds([newParcel.id]);
    polygonsRef.current[newParcel.id] = poly;
    poly.addListener('click', (e: google.maps.MapMouseEvent) => {
      const additive = !!(e.domEvent as MouseEvent)?.shiftKey;
      toggleSelection(newParcel.id, additive);
    });
    attachPathListeners(newParcel.id, poly);
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
  }, [map, parcels, pushHistoryEntry, bumpParcels, attachPathListeners, toggleSelection, clearPreview]);

  useEffect(() => {
    if (!map) return;
    for (const l of listenersRef.current) google.maps.event.removeListener(l);
    listenersRef.current = [];
    for (const poly of Object.values(polygonsRef.current)) poly.setMap(null);
    polygonsRef.current = {};
    splitLineRef.current?.setMap(null);
    splitLineRef.current = null;

    parcels.forEach((p, i) => renderParcelOnMap(p, i, map, selectedParcelIds, activeId));
  }, [map, parcels, parcelsRevision, selectedParcelIds, activeId, splitMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persistent map listeners for click-to-add-vertex parcel drawing
  // (replaces the drawing-library manager). Registered once per map;
  // the handlers read `isDrawingRef` so they no-op outside drawing mode
  // (and split mode, which never co-exists with drawing — startDrawing
  // exits split mode and entering split mode clears isDrawing).
  useEffect(() => {
    if (!map) return;

    const onClick = (e: google.maps.MapMouseEvent) => {
      if (!isDrawingRef.current) return;
      const latLng = e.latLng;
      if (!latLng) return;
      let point = { lat: latLng.lat(), lng: latLng.lng() };

      const verts = drawVerticesRef.current;
      if (verts.length >= 3 && google.maps.geometry) {
        const first = new google.maps.LatLng(verts[0].lat, verts[0].lng);
        const here = new google.maps.LatLng(point.lat, point.lng);
        const metres = google.maps.geometry.spherical.computeDistanceBetween(first, here);
        if (metres < 40) {
          finishDrawing(verts);
          return;
        }
      }

      const cfg = snapConfigRef.current;
      if (cfg.snapEnabled || cfg.alignmentGuidesEnabled || cfg.orthogonalConstraintEnabled) {
        const tuples = verts.map((v) => [v.lat, v.lng] as [number, number]);
        const prevVertex = tuples.length >= 1 ? tuples[tuples.length - 1] : undefined;
        const nextVertex = tuples.length >= 2 ? tuples[0] : undefined;
        const snapped = snapAndAlignVertex({
          candidate: [point.lat, point.lng],
          activePolygon: tuples,
          prevVertex,
          nextVertex,
          referencePolygons: getReferencePolygonsForParcel(undefined),
          snapToVerticesAndEdges: cfg.snapEnabled,
          enableAlignmentGuides: cfg.alignmentGuidesEnabled,
          enableOrthogonalConstraint: cfg.orthogonalConstraintEnabled,
        });
        point = { lat: snapped.point[0], lng: snapped.point[1] };
        setActiveGuides(snapped.guides);
      } else {
        setActiveGuides([]);
      }

      const next = [...verts, point];
      drawVerticesRef.current = next;
      setDrawVertices(next);
    };

    const onDblClick = (e: google.maps.MapMouseEvent) => {
      if (!isDrawingRef.current) return;
      const verts = drawVerticesRef.current;
      if (verts.length >= 3) {
        if (e.stop) e.stop();
        finishDrawing(verts);
      }
    };

    const clickListener = map.addListener('click', onClick);
    const dblListener = map.addListener('dblclick', onDblClick);
    drawListenersRef.current = [clickListener, dblListener];

    return () => {
      for (const l of drawListenersRef.current) google.maps.event.removeListener(l);
      drawListenersRef.current = [];
    };
  }, [map, finishDrawing]);

  // Live preview polygon while placing vertices.
  useEffect(() => {
    if (!map || !isDrawing || drawVertices.length === 0) {
      clearPreview();
      return;
    }
    clearPreview();
    previewRef.current = new google.maps.Polygon({
      paths: drawVertices,
      strokeColor: PARCEL_COLORS[parcels.length % PARCEL_COLORS.length].stroke,
      strokeWeight: 2,
      fillColor: PARCEL_COLORS[parcels.length % PARCEL_COLORS.length].stroke,
      fillOpacity: 0.2,
      map,
    });
    return () => clearPreview();
  }, [map, isDrawing, drawVertices, parcels.length, clearPreview]);

  useEffect(() => {
    if (!map || !splitMode) return;

    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      const point = { lat, lng };

      if (!splitLineStart) {
        setSplitLineStart(point);
        splitLineRef.current?.setMap(null);
        splitLineRef.current = new google.maps.Polyline({
          path: [point, point],
          strokeColor: PRIMARY_STROKE,
          strokeWeight: 2,
          map,
        });
        return;
      }

      const parcelId = selectedParcelIds[0];
      if (!parcelId) return;
      const before = cloneParcels(parcels);
      // Stage split locally; nothing is persisted until explicit confirmation (handleConfirm).
      const after = splitParcelLocal(parcels, parcelId, splitLineStart, point);
      if (!after) return;

      pushHistoryEntry(before, after, 'split');
      bumpParcels(after);
      setSplitMode(false);
      setSplitLineStart(null);
      splitLineRef.current?.setMap(null);
      splitLineRef.current = null;
      setSelectedParcelIds([]);
      setActiveId(after.find(p => p.id !== parcelId)?.id ?? after[0]?.id ?? '');
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [
    map,
    splitMode,
    splitLineStart,
    selectedParcelIds,
    parcels,
    pushHistoryEntry,
    bumpParcels,
  ]);

  const handleUndo = useCallback(() => {
    const entry = undoStack[undoStack.length - 1];
    if (!entry) return;
    setUndoStack(s => s.slice(0, -1));
    setRedoStack(s => [...s.slice(-19), entry]);
    bumpParcels(entry.before);
    setSelectedParcelIds([]);
    setActiveId(entry.before[0]?.id ?? '');
    setSplitMode(false);
    setSplitLineStart(null);
  }, [undoStack, bumpParcels]);

  const handleRedo = useCallback(() => {
    const entry = redoStack[redoStack.length - 1];
    if (!entry) return;
    setRedoStack(s => s.slice(0, -1));
    setUndoStack(s => [...s.slice(-19), entry]);
    bumpParcels(entry.after);
    setSelectedParcelIds([]);
    setActiveId(entry.after[0]?.id ?? '');
  }, [redoStack, bumpParcels]);

  const handleMerge = useCallback(async () => {
    if (!canMerge) return;
    setMergeBusy(true);
    try {
      const before = cloneParcels(parcels);
      // Stage merge locally; nothing is persisted until explicit confirmation (handleConfirm).
      const after = mergeParcelsLocal(parcels, selectedParcelIds);
      if (!after) return;

      pushHistoryEntry(before, after, 'merge');
      bumpParcels(after);
      const targetId = selectedParcelIds[0]!;
      setSelectedParcelIds([]);
      setActiveId(targetId);
    } finally {
      setMergeBusy(false);
    }
  }, [
    selectedParcelIds,
    parcels,
    canMerge,
    pushHistoryEntry,
    bumpParcels,
  ]);

  const enterSplitMode = useCallback(() => {
    if (!canSplit) return;
    setSplitMode(true);
    setSplitLineStart(null);
    // Cancel any in-progress new-parcel drawing — split mode and drawing
    // mode are mutually exclusive (both consume map clicks).
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
  }, [canSplit, clearPreview]);

  const exitSplitMode = useCallback(() => {
    setSplitMode(false);
    setSplitLineStart(null);
    splitLineRef.current?.setMap(null);
    splitLineRef.current = null;
  }, []);

  useEffect(() => {
    const el = drawerRef.current;
    if (!el) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }
      if (mod && (e.key.toLowerCase() === 'z' && e.shiftKey || e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        handleRedo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedParcelIds(parcels.map(p => p.id));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        exitSplitMode();
        return;
      }
      if (e.key.toLowerCase() === 'm' && canMerge) {
        e.preventDefault();
        void handleMerge();
        return;
      }
      if (e.key.toLowerCase() === 's' && canSplit) {
        e.preventDefault();
        enterSplitMode();
      }
    };

    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [canMerge, canSplit, handleMerge, handleRedo, handleUndo, enterSplitMode, exitSplitMode, parcels]);

  const handleRemoveActive = () => {
    if (parcels.length <= 1) return;
    const before = cloneParcels(parcels);
    const poly = polygonsRef.current[activeId];
    poly?.setMap(null);
    delete polygonsRef.current[activeId];
    const next = parcels.filter(p => p.id !== activeId);
    pushHistoryEntry(before, next, 'edit');
    bumpParcels(next);
    setSelectedParcelIds([]);
    setActiveId(next[0]?.id ?? '');
  };

  const updateMeta = (parcelId: string, patch: Partial<SiteBoundaryParcel>) => {
    const before = cloneParcels(parcels);
    const next = parcels.map(p => (p.id === parcelId ? { ...p, ...patch } : p));
    pushHistoryEntry(before, next, 'edit');
    bumpParcels(next);
  };

  const startDrawing = () => {
    exitSplitMode();
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
    isDrawingRef.current = true;
    setIsDrawing(true);
  };

  const undoLastVertex = () => {
    const next = drawVerticesRef.current.slice(0, -1);
    drawVerticesRef.current = next;
    setDrawVertices(next);
  };

  const cancelDrawing = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
  };

  const targetParcelForSquare = useMemo(() => {
    const id = selectedParcelIds[0] ?? activeId ?? parcels[0]?.id;
    return parcels.find((p) => p.id === id) ?? null;
  }, [selectedParcelIds, activeId, parcels]);

  const handlePreviewSquareActiveParcel = useCallback(() => {
    if (!targetParcelForSquare || targetParcelForSquare.polygon.length < 3) return;
    const tuples = targetParcelForSquare.polygon.map(
      (pt) => [pt.lat, pt.lng] as [number, number],
    );
    setSquarePreview(previewSquarePolygonCorners(tuples));
    setSquarePreviewMode('square');
  }, [targetParcelForSquare]);

  const handlePreviewRectangleActiveParcel = useCallback(() => {
    if (!targetParcelForSquare || targetParcelForSquare.polygon.length < 3) return;
    const tuples = targetParcelForSquare.polygon.map(
      (pt) => [pt.lat, pt.lng] as [number, number],
    );
    setSquarePreview(previewAxisAlignedRectangle(tuples, true));
    setSquarePreviewMode('rectangle');
  }, [targetParcelForSquare]);

  const handleApplySquareActiveParcel = useCallback(() => {
    if (!targetParcelForSquare || !squarePreview || squarePreview.squaredPoints.length < 3) return;
    const before = cloneParcels(parcels);
    const coords = squarePreview.squaredPoints.map(([lat, lng]) => ({ lat, lng }));
    const next = parcels.map((p) =>
      p.id === targetParcelForSquare.id
        ? {
            ...p,
            polygon: coords,
            area: parseFloat(parcelArea(coords, p.holes).toFixed(1)),
            perimeter: parseFloat(parcelPerimeter(coords, p.holes).toFixed(2)),
            centroid: polygonCentroid(coords),
          }
        : p,
    );
    const invalid = next.map(holeGeometryError).find(Boolean);
    if (invalid) { setGeometryError(invalid); return; }
    setGeometryError(null);
    pushHistoryEntry(before, next, 'edit');
    bumpParcels(next);
    setSquarePreview(null);
    setSquarePreviewMode(null);
    setActiveGuides([]);
  }, [targetParcelForSquare, squarePreview, parcels, pushHistoryEntry, bumpParcels]);

  const handleCancelSquareActiveParcel = useCallback(() => {
    setSquarePreview(null);
    setSquarePreviewMode(null);
  }, []);

  const handleConfirm = async () => {
    const invalid = parcels.map(holeGeometryError).find(Boolean);
    if (invalid) { setGeometryError(invalid); return; }
    setGeometryError(null);
    setConfirming(true);
    try {
      let elevationMin: number | null = null;
      let elevationMax: number | null = null;
      let elevationAvg: number | null = null;
      const first = parcels[0];
      if (first && first.polygon.length >= 3) {
        const elev = await fetchPolygonElevationsCascading(first.polygon);
        if (elev.kind === 'success') {
          elevationMin = elev.stats.min;
          elevationMax = elev.stats.max;
          elevationAvg = elev.stats.avg;
        }
      }
      await onConfirm({
        boundary: first?.polygon ?? [],
        areaAcres: aggregate.totalArea,
        perimeterKm: aggregate.totalPerimeter,
        centroid: aggregate.aggregateCentroid,
        elevationMin,
        elevationMax,
        elevationAvg,
        parcels,
      });
      if (siteId) {
        for (const entry of undoStack) {
          if (entry.kind === 'merge' || entry.kind === 'split') {
            const telemetry = buildTopologyTelemetry(entry.before, entry.after);
            logParcelTopologyChange(
              siteId,
              entry.kind === 'merge' ? 'parcel-merged' : 'parcel-split',
              telemetry,
            );
          }
        }
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      ref={drawerRef}
      className="space-y-3 outline-none"
      data-testid="multi-parcel-drawer"
      tabIndex={0}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="type-meta font-bold text-text-secondary">
          Multi-parcel boundary ({parcels.length} parcels)
          {splitMode ? ' · split mode' : ''}
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={handleUndo} disabled={undoStack.length === 0} aria-label="Undo">
            <Undo2 className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleRedo} disabled={redoStack.length === 0} aria-label="Redo">
            <Redo2 className="w-3.5 h-3.5" />
          </Button>
          {canMerge && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleMerge()}
              disabled={mergeBusy}
              aria-label="Merge selected parcels"
              data-testid="merge-parcels-btn"
            >
              <Merge className="w-3.5 h-3.5 mr-1" /> Merge
            </Button>
          )}
          {canSplit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={enterSplitMode}
              aria-label="Split selected parcel"
              data-testid="split-parcel-btn"
            >
              <Scissors className="w-3.5 h-3.5 mr-1" /> Split
            </Button>
          )}
          {isDrawing ? (
            <>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => finishDrawing(drawVertices)}
                disabled={drawVertices.length < 3}
                data-testid="parcel-draw-finish"
              >
                <Check className="w-3.5 h-3.5 mr-1" /> Finish ({drawVertices.length})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={undoLastVertex}
                disabled={drawVertices.length === 0}
                aria-label="Undo last vertex"
                data-testid="parcel-draw-undo"
              >
                <Undo2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelDrawing}
                aria-label="Cancel drawing"
                data-testid="parcel-draw-cancel"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={startDrawing} disabled={splitMode}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add parcel
            </Button>
          )}
        </div>
      </div>

      {isDrawing && (
        <p className="text-xs text-primary font-medium" data-testid="parcel-draw-active">
          Click the map to add vertices · double-click or click the first point to finish · {drawVertices.length} placed (3 min).
        </p>
      )}

      {splitMode && (
        <p className="text-xs text-primary font-medium" data-testid="split-mode-active">
          Click two points on the map to draw a split line across the selected parcel. Press Esc to cancel.
        </p>
      )}

      <div className="px-2 py-1.5 border border-border bg-state-hover text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-text-secondary">
          Total: {aggregate.totalArea.toFixed(0)} ac across {parcels.length} parcels
          {aggregate.isDisjoint ? ' (disjoint)' : ''}
          {selectedParcelIds.length > 0 ? ` · ${selectedParcelIds.length} selected` : ''}
        </span>
      </div>

      {/* Opt-in Align / Square / Snap toolbar (#244 #issuecomment-6607686195, #issuecomment-6607716290) */}
      <div
        data-testid="multi-parcel-alignment-toolbar"
        className="flex flex-wrap items-center gap-1.5 p-2 border border-border-subtle bg-background rounded-md"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-text-muted mr-1">
          Tools:
        </span>
        <Button
          type="button"
          variant={snapEnabled ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2 rounded text-[10px] uppercase tracking-wider"
          onClick={() => setSnapEnabled((v) => !v)}
          data-testid="multi-parcel-tool-snap"
          aria-pressed={snapEnabled}
        >
          <Magnet className="w-3 h-3 mr-1" /> Snap
        </Button>
        <Button
          type="button"
          variant={alignmentGuidesEnabled ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2 rounded text-[10px] uppercase tracking-wider"
          onClick={() => setAlignmentGuidesEnabled((v) => !v)}
          data-testid="multi-parcel-tool-align-guides"
          aria-pressed={alignmentGuidesEnabled}
        >
          <Compass className="w-3 h-3 mr-1" /> Align Guides
        </Button>
        <Button
          type="button"
          variant={orthogonalConstraintEnabled ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2 rounded text-[10px] uppercase tracking-wider"
          onClick={() => setOrthogonalConstraintEnabled((v) => !v)}
          data-testid="multi-parcel-tool-ortho"
          aria-pressed={orthogonalConstraintEnabled}
        >
          90° Ortho
        </Button>
        {targetParcelForSquare && !isDrawing && !splitMode && (
          <>
            <Button
              type="button"
              variant={squarePreviewMode === 'square' ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 rounded text-[10px] uppercase tracking-wider"
              onClick={handlePreviewSquareActiveParcel}
              data-testid="multi-parcel-tool-square-preview"
            >
              <Square className="w-3 h-3 mr-1" /> Square Parcel
            </Button>
            <Button
              type="button"
              variant={squarePreviewMode === 'rectangle' ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 rounded text-[10px] uppercase tracking-wider"
              onClick={handlePreviewRectangleActiveParcel}
              data-testid="multi-parcel-tool-rectangle-preview"
            >
              Rectangle
            </Button>
          </>
        )}
      </div>

      {geometryError && <p role="alert" className="text-xs text-danger">{geometryError}</p>}
      {selectedHasHoles && <p role="status" className="text-xs text-text-secondary">Merge and split are unavailable for parcels with exclusion rings. The saved exclusions are preserved.</p>}

      {squarePreview && (
        <div
          data-testid="multi-parcel-square-preview-banner"
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border border-primary/40 bg-primary/10 rounded-md text-xs"
        >
          <span className="font-mono text-[11px] text-text-primary">
            Preview ({targetParcelForSquare?.label ?? 'Active Parcel'}): max corner deviation{' '}
            {squarePreview.beforeMaxRightAngleDeviationDeg}° →{' '}
            {squarePreview.afterMaxRightAngleDeviationDeg}° · area Δ {squarePreview.areaDeltaPct}%
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-7 px-2.5 rounded text-[10px] uppercase tracking-wider"
              onClick={handleApplySquareActiveParcel}
              data-testid="multi-parcel-square-apply"
            >
              <Check className="w-3 h-3 mr-1" /> Apply
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 rounded text-[10px] uppercase tracking-wider"
              onClick={handleCancelSquareActiveParcel}
              data-testid="multi-parcel-square-cancel"
            >
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      <div className={`h-[45vh] min-h-[260px] border border-border relative ${splitMode ? 'cursor-crosshair' : ''}`}>
        <Map
          id="multi-parcel-map"
          mapId="bf51a910020fa25a"
          defaultCenter={center}
          defaultZoom={15}
          mapTypeId="hybrid"
          gestureHandling="greedy"
        >
          {existingLayers && existingLayers.length > 0 && (
            <SiteLayerOverlays layers={existingLayers} strokeOpacity={0.4} fillOpacity={0.1} strokeWeight={1.5} />
          )}
          {studyAreaPolygons.length > 0 && (
            <StudyAreaSelectedOverlays polygons={studyAreaPolygons} />
          )}
          <BoundaryAlignmentPreviewOverlay preview={squarePreview} guides={activeGuides} />
        </Map>
        <TouchHintOverlay />
      </div>

      <div className="space-y-2 max-h-[180px] overflow-y-auto" data-testid="multi-parcel-panel">
        {parcels.map((p, i) => {
          const c = PARCEL_COLORS[i % PARCEL_COLORS.length];
          const active = p.id === activeId;
          const selected = selectedParcelIds.includes(p.id);
          return (
            <div
              key={p.id}
              className={`flex items-start gap-2 p-2 border ${
                selected ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : active ? 'border-border-subtle bg-state-hover' : 'border-border-subtle'
              }`}
              onClick={e => toggleSelection(p.id, e.shiftKey)}
              role="button"
              tabIndex={0}
              aria-selected={selected}
              onKeyDown={e => {
                if (e.key === 'Enter') toggleSelection(p.id, e.shiftKey);
              }}
            >
              <span className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: c.dot }} />
              <div className="flex-1 min-w-0 space-y-1">
                <input
                  className="w-full text-xs font-semibold bg-transparent border-b border-transparent focus:border-border outline-none"
                  value={p.label ?? ''}
                  onChange={e => updateMeta(p.id, { label: e.target.value })}
                  onClick={e => e.stopPropagation()}
                  aria-label={`Parcel ${i + 1} label`}
                />
                <div className="flex flex-wrap gap-2 text-[10px] text-text-muted">
                  <span className="inline-flex items-center gap-0.5"><Ruler className="w-3 h-3" />{p.area.toFixed(0)} ac</span>
                  <span>{p.perimeter.toFixed(1)} km</span>
                </div>
                <select
                  className="text-[10px] border border-border-subtle bg-background px-1 py-0.5"
                  value={p.intendedUse ?? ''}
                  onChange={e => updateMeta(p.id, { intendedUse: (e.target.value || undefined) as SiteBoundaryParcel['intendedUse'] })}
                  onClick={e => e.stopPropagation()}
                  aria-label={`Parcel ${i + 1} intended use`}
                >
                  <option value="">Intended use…</option>
                  {(['datacenter', 'substation', 'water', 'buffer', 'access', 'storage', 'other'] as const).map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              {parcels.length > 1 && active && (
                <Button type="button" variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleRemoveActive(); }} aria-label="Remove parcel">
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {onSaveLabel && (
        <AddressLabelOverrideControl
          addressLabel={addressLabel}
          canonicalAddress={canonicalAddress ?? ''}
          onSave={onSaveLabel}
        />
      )}

      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onSkip} className="flex-1">
          Skip boundary
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={parcels.length === 0 || confirming} className="flex-1">
          <Check className="w-4 h-4 mr-1" />
          {confirming ? 'Confirming…' : 'Confirm parcels'}
        </Button>
      </div>
    </div>
  );
};
